import React, { createContext, useContext, useState, useCallback } from 'react';

type MinimizedPanelsContextType = {
  registerExpandHandler: (panelId: string, handler: () => void, title: string) => void;
  unregisterPanel: (panelId: string) => void;
  getExpandHandler: (panelId: string) => (() => void) | undefined;
  getPanelTitle: (panelId: string) => string | undefined;
}

const MinimizedPanelsContext = createContext<MinimizedPanelsContextType | null>(null);

/**
 * Provider component that manages minimized panel expand handlers and titles
 */
export const MinimizedPanelsProvider = ({ children }: { children: React.ReactNode }) => {
  const [handlers, setHandlers] = useState<Record<string, () => void>>({});
  const [titles, setTitles] = useState<Record<string, string>>({});

  const registerExpandHandler = useCallback((panelId: string, handler: () => void, title: string) => {
    setHandlers(prev => ({ ...prev, [panelId]: handler }));
    setTitles(prev => ({ ...prev, [panelId]: title }));
  }, []);

  const unregisterPanel = useCallback((panelId: string) => {
    setHandlers(prev => {
      const next = { ...prev };
      delete next[panelId];
      return next;
    });
    setTitles(prev => {
      const next = { ...prev };
      delete next[panelId];
      return next;
    });
  }, []);

  const getExpandHandler = useCallback((panelId: string) => {
    return handlers[panelId];
  }, [handlers]);

  const getPanelTitle = useCallback((panelId: string) => {
    return titles[panelId];
  }, [titles]);

  return (
    <MinimizedPanelsContext.Provider value={{
      registerExpandHandler,
      unregisterPanel,
      getExpandHandler,
      getPanelTitle,
    }}>
      {children}
    </MinimizedPanelsContext.Provider>
  );
};

/**
 * Hook to access minimized panels context
 */
export const useMinimizedPanels = () => {
  const context = useContext(MinimizedPanelsContext);
  if (!context) {
    throw new Error('useMinimizedPanels must be used within MinimizedPanelsProvider');
  }
  return context;
};

